import { ArrowRight, Clock3, IdCard, Ship, Sun, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EnquiryDialog } from "@/components/site/EnquiryDialog";
import { BookingEnquiry } from "@/components/site/BookingEnquiry";
import { PageHero, SectionHead } from "@/components/site/PageHero";
import { IMG } from "@/lib/constants";
import { Seo, breadcrumb } from "@/components/site/Seo";

const ROUTES = [
  { from: "Port Blair", to: "Swaraj Dweep (Havelock)", note: "The most popular sector, for Radhanagar and Elephant Beach.", img: IMG.radhanagar },
  { from: "Swaraj Dweep (Havelock)", to: "Shaheed Dweep (Neil Island)", note: "A short hop between the two most-visited islands.", img: IMG.boat },
  { from: "Shaheed Dweep (Neil Island)", to: "Port Blair", note: "Return to Port Blair for your flight home.", img: IMG.jetty },
];
const STEPS = ["Share your route, travel date and passengers", "We check ferry timings and seat options for you", "Confirm your preferred ferry and complete payment", "We confirm the booking and share the details"];
const TIPS = [
  { icon: IdCard, t: "Carry photo ID", d: "Original government photo ID is needed for every passenger." },
  { icon: Clock3, t: "Reach the jetty early", d: "Plan to be at the jetty well before departure for check-in." },
  { icon: Sun, t: "Book early in peak season", d: "October to May is busy, so seats fill up quickly." },
  { icon: Waves, t: "Weather can change plans", d: "Ferry schedules may change with sea and weather conditions." },
];
const MSG = (r) => `Ferry booking enquiry - Route: ${r.from} to ${r.to}, Travel date: , Passengers: `;

const RouteCard = ({ r, i }) => (
  <article className="group rounded-3xl border border-slate-200 bg-white overflow-hidden flex flex-col hover:shadow-xl transition-shadow duration-300" data-testid={`ferry-route-${i}`}>
    <div className="relative h-44 overflow-hidden">
      <img src={r.img} alt={`${r.from} to ${r.to}`} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-navy"><Ship className="h-3.5 w-3.5 text-ocean" />Ferry route</span>
    </div>
    <div className="p-6 flex flex-col flex-1">
      <p className="font-semibold text-navy text-lg leading-snug">{r.from} <ArrowRight className="inline h-4 w-4 text-ocean mx-1" /> {r.to}</p>
      <p className="mt-2 text-sm text-slate-600 flex-1">{r.note}</p>
      <EnquiryDialog title="Andaman Ferry Booking" defaultMessage={MSG(r)} tripType="Ferry"
        trigger={<Button className="mt-5 h-11 rounded-full bg-ocean hover:bg-navy font-semibold" data-testid={`ferry-route-${i}-enquire`}>Enquire for this route</Button>} />
    </div>
  </article>
);

export default function FerryBooking() {
  return (
    <div data-testid="ferry-booking-page">
      <Seo title="Andaman Ferry Booking | Port Blair to Havelock & Neil Island | Ocean Wings Travel"
        description="Book Andaman ferry tickets between Port Blair, Havelock (Swaraj Dweep) and Neil Island (Shaheed Dweep). Government and private ferries, with hotels and transfers on request."
        keywords="Andaman ferry booking, Port Blair to Havelock ferry, Havelock to Neil ferry, Neil Island ferry, Andaman ferry tickets"
        path="/ferry-booking" jsonLd={[breadcrumb([["Home", "/"], ["Ferry Booking", "/ferry-booking"]])]} />
      <PageHero img={IMG.jetty} alt="Boats at an Andaman jetty" eyebrow="Andaman Ferry Booking" testId="ferry-hero"
        title="Ferry booking for Port Blair, Havelock & Neil Island"
        subtitle="Ocean Wings Travel books inter-island ferries between Port Blair, Swaraj Dweep (Havelock) and Shaheed Dweep (Neil Island), on their own or with your hotels and transfers.">
        <a href="#ferry-enquiry" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-white text-navy font-semibold hover:bg-aqua transition-colors" data-testid="ferry-hero-book">Book Ferry <ArrowRight className="h-4 w-4" /></a>
      </PageHero>
      <section className="container-x pt-20">
        <SectionHead eyebrow="Popular routes" title="Island-hopping made simple" subtitle="Tell us your route and date, and we'll suggest the best ferry timings for your plan." />
        <div className="mt-10 grid gap-6 md:grid-cols-3">{ROUTES.map((r, i) => <RouteCard key={r.from} r={r} i={i} />)}</div>
      </section>
      <section className="container-x pt-20 grid gap-10 lg:grid-cols-2">
        <div>
          <SectionHead eyebrow="How it works" title="Book in four easy steps" />
          <ol className="mt-8 space-y-4">
            {STEPS.map((s, i) => (
              <li key={s} className="flex gap-4 items-start rounded-2xl border border-slate-200 bg-white p-5" data-testid={`ferry-step-${i}`}>
                <span className="h-9 w-9 shrink-0 rounded-full bg-ocean text-white grid place-items-center font-mono text-sm font-semibold">{i + 1}</span>
                <p className="pt-1.5 text-slate-700">{s}</p>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-3xl bg-mist p-6 sm:p-10">
          <p className="eyebrow">Good to know</p>
          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            {TIPS.map(({ icon: Icon, t, d }) => (
              <div key={t}><span className="h-11 w-11 rounded-xl bg-white text-ocean grid place-items-center shadow-sm"><Icon className="h-5 w-5" /></span>
                <p className="mt-3 font-semibold text-navy">{t}</p><p className="mt-1 text-sm text-slate-600">{d}</p></div>
            ))}
          </div>
        </div>
      </section>
      <BookingEnquiry id="ferry-enquiry" idPrefix="ferry-enquiry" title="Request your ferry tickets" tripType="Ferry"
        message="Ferry booking enquiry - Route: , Travel date: , Passengers: "
        wa="Hi Ocean Wings Travel, I'd like to book an Andaman ferry. Route: , Date: , Passengers: " />
    </div>
  );
}
